import { useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { Box, CircularProgress, Tab, Tabs } from "@mui/material";
import { getAtlStatus, getAtlHistory } from "../../Actions/AtlAction";
import { globalStyle } from "../../global/Utils";
import AtlList from "./AtlList";
import AtlUpload from "./AtlUpload";

// How often the server is asked how a running upload / import is getting on.
const POLL_INTERVAL_MS = 2000;

// FBR Active Taxpayer List. Everyone can search the list that is live; the
// Upload tab - the daily file that replaces it, and how that import is getting
// on - is for administrators only. A user's page never asks for the import
// status or history: the server refuses those with a 401, and the app treats
// any 401 as "logged out".
const ActiveTaxpayers = () => {
  const dispatch = useDispatch();
  const [activeTab, setActiveTab] = useState("list");

  const userDetails = useSelector((state) => state?.HeaderReducer?.userDetails);
  const activeImportId = useSelector(
    (state) => state.AtlReducer?.activeImport?.id || "",
  );
  const uploadStatus = useSelector((state) => state.AtlReducer?.uploadStatus);
  const isAdmin = userDetails?.role === "admin";

  useEffect(() => {
    if (!isAdmin) return;
    dispatch(getAtlStatus());
    dispatch(getAtlHistory());
  }, [dispatch, isAdmin]);

  // While something is running - here or started by another admin - keep the
  // progress fresh. Nothing is polled when the list is simply sitting there.
  const isWatching = Boolean(activeImportId) || uploadStatus === "uploading";
  useEffect(() => {
    if (!isAdmin || !isWatching) return undefined;
    const timer = setInterval(() => dispatch(getAtlStatus()), POLL_INTERVAL_MS);
    return () => {
      clearInterval(timer);
      // Whatever was running has just ended: show it in the history.
      dispatch(getAtlHistory());
      // The server may take a couple of seconds to switch every one of its
      // processes to a newly published list, so look once more after that.
      setTimeout(() => dispatch(getAtlStatus()), POLL_INTERVAL_MS + 1000);
    };
  }, [dispatch, isAdmin, isWatching]);

  if (!userDetails) {
    return (
      <div className="flex items-center justify-center h-screen bg-gray-100">
        <CircularProgress />
      </div>
    );
  }

  return (
    <Box className="p-4">
      <Box sx={{ mb: 1 }}>
        <Box
          component="h1"
          sx={{
            fontFamily: globalStyle.fontFamilyHeading,
            fontWeight: 700,
            color: globalStyle.bgColorBlue,
            fontSize: {
              xs: globalStyle.headingFontSizeSmall, // mobile
              sm: globalStyle.headingFontSize, // tablet+
            },
            lineHeight: 1.2,
          }}
        >
          Active Taxpayers
        </Box>
        <Box
          component="p"
          sx={{
            mt: 0.5,
            fontFamily: globalStyle.fontFamilyParagraph,
            fontSize: {
              xs: 12,
              sm: 14,
            },
            color: "#666",
          }}
        >
          {isAdmin
            ? "Search FBR's Active Taxpayer List and upload the daily file"
            : "Search FBR's Active Taxpayer List"}
        </Box>
      </Box>

      {/* Not an administrator: the list, and nothing that leads to the upload. */}
      {!isAdmin && <AtlList />}

      {isAdmin && (
        <>
          <Tabs
            value={activeTab}
            onChange={(event, value) => setActiveTab(value)}
            sx={{
              mb: 2,
              borderBottom: `1px solid ${globalStyle.curveLightColor}`,
              "& .MuiTab-root": {
                textTransform: "none",
                fontFamily: globalStyle.fontFamilyHeading,
                fontWeight: 600,
                fontSize: 15,
              },
            }}
          >
            <Tab value="list" label="Taxpayer List" />
            <Tab
              value="upload"
              label={isWatching ? "Upload File (in progress)" : "Upload File"}
            />
          </Tabs>

          {/* Both stay mounted so switching tabs loses neither the search that
          was typed nor the file that was picked. */}
          <Box sx={{ display: activeTab === "list" ? "block" : "none" }}>
            <AtlList onGoToUpload={() => setActiveTab("upload")} />
          </Box>
          <Box sx={{ display: activeTab === "upload" ? "block" : "none" }}>
            <AtlUpload />
          </Box>
        </>
      )}
    </Box>
  );
};

export default ActiveTaxpayers;
